import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

import { isRateLimited } from '@core/helpers/api-error.helper';

/**
 * How many times one request is re-sent before its 429 is allowed to surface. Three attempts
 * spans roughly the length of a short throttle window without leaving a screen waiting for long.
 */
export const RATE_LIMIT_MAX_RETRIES = 3;

/** First backoff step; each further attempt doubles it. */
export const RATE_LIMIT_BASE_DELAY_MS = 1000;

/** Nothing waits longer than this between attempts, whatever `Retry-After` asks for. */
export const RATE_LIMIT_MAX_DELAY_MS = 8000;

/**
 * Spread, in milliseconds, added to each wait.
 *
 * The builder trips the limiter with a burst of requests that all fail at once, so a fixed
 * backoff would re-send them all at once too and trip it again. The jitter pulls them apart.
 */
const RATE_LIMIT_JITTER_MS = 250;

/**
 * A fraction in `[0, 1)` from the platform's secure generator.
 *
 * Nothing here is a secret, so `Math.random` would do the job — but this codebase already reaches
 * for `crypto.getRandomValues` wherever it needs randomness, and a scanner cannot tell a harmless
 * jitter from a token by looking at the call. Falls back to `Math.random` only where the secure
 * generator is absent, which is no worse than having started there.
 */
function randomFraction(): number {
  const cryptoObj = globalThis.crypto;
  if (typeof cryptoObj?.getRandomValues !== 'function') {
    return Math.random(); // NOSONAR — jitter only, and only when no secure generator exists
  }
  const slice = new Uint32Array(1);
  cryptoObj.getRandomValues(slice);
  return (slice[0] ?? 0) / 2 ** 32;
}

/**
 * Reads `Retry-After`, which the limiter may send as a number of seconds or as an HTTP date.
 * Returns null when the header is absent or unreadable, leaving the caller on plain backoff.
 */
export function retryAfterDelayMs(header: string | null): number | null {
  if (header === null) return null;

  const trimmed = header.trim();
  if (trimmed.length === 0) return null;

  const seconds = Number(trimmed);
  if (Number.isFinite(seconds)) return seconds > 0 ? seconds * 1000 : 0;

  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  // A date already in the past means "go now", not "go back in time".
  return Math.max(0, at - Date.now());
}

/** The wait before attempt `attempt` (1-based), honouring `Retry-After` when the limiter sent one. */
export function rateLimitDelayMs(attempt: number, retryAfter: string | null): number {
  const asked = retryAfterDelayMs(retryAfter);
  const backoff = RATE_LIMIT_BASE_DELAY_MS * 2 ** (attempt - 1);
  const base = Math.min(asked ?? backoff, RATE_LIMIT_MAX_DELAY_MS);
  return base + randomFraction() * RATE_LIMIT_JITTER_MS;
}

/**
 * Re-sends a request the API's per-IP rate limiter turned away (429).
 *
 * The cart builder opens with a burst of reads — the cart, its contacts, the event catalogues and
 * one add-on catalogue per show — and in non-production that burst is enough to trip the limiter,
 * which surfaced as "Too many requests just now" on an ordinary page load. Waiting and asking
 * again is what the limiter is asking for, so it is done here rather than left to each screen.
 *
 * Every method is retried, not just the safe ones: a 429 is the limiter refusing the request
 * outright, so nothing was read or written and re-sending cannot duplicate anything. That is the
 * same reasoning behind {@link isRateLimited}'s own note.
 *
 * Runs closest to the network, ahead of the interceptors that report failures, so a retried
 * request that succeeds never reaches them as an error at all.
 */
@Injectable()
export class RateLimitRetryInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      retry({
        count: RATE_LIMIT_MAX_RETRIES,
        delay: (err: unknown, attempt: number) => {
          if (!(err instanceof HttpErrorResponse) || !isRateLimited(err)) {
            return throwError(() => err);
          }
          // `timer` emitting once is what tells `retry` to go again.
          return timer(rateLimitDelayMs(attempt, err.headers.get('Retry-After')));
        },
      }),
    );
  }
}
